import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import 'rxjs/add/operator/take';

import * as _ from 'lodash';

import {AngularFirestore, AngularFirestoreCollection} from 'angularfire2/firestore';

import {Location} from "../models/location";
import {ThinningMeasurement} from "../models/thinning-measurement";
import {SprayRecord} from "../models/spray-record";
import {IrrigationRecord} from "../models/irrigation-record";
import {ThinningDataset} from "../models/thinning-dataset";

/**
 * Service to wrap all firebase-related data operations.
 */
@Injectable()
export class Data {

    // these are for the current authenticated user, or the user being masked as...
    userEmail: string;
    userKey: string;
    userIsAdmin: boolean = false;
    userIsSuperAdmin: boolean = false;

    // these are for the admin who is masking as another user...
    maskingAdminEmail: string;
    maskingAdminKey: string;

    usersCollection: AngularFirestoreCollection<any>;
    users: Observable<any[]>;

    locationsCollection: AngularFirestoreCollection<any>;
    locations: Observable<Location[]>;

    constructor(public afs: AngularFirestore) {
    }

    // Called when the user authState changes to a valid user, either as a result of login or retrieval
    // of a locally-stored firebase session.
    // This is called from the WelcomePage component, as that also needs to react to this change by skipping
    // the login page (and it needs access to the NavController, so we can't move the entire change handler here).
    handleUserAuthentication(user) {
        console.log('handleUserAuthentication', user);
        this.userEmail = user.email;
        this.userKey = this.userEmail;

        // if a doc exists in the admins collection for this user, they are an admin
        const ap = this.afs.doc<any>(`/admins/${user.email}`).valueChanges().subscribe((val) => {
            // NB: val is undefined if no doc exists, or {} if it does (doc has no properties)
            if (val) {
                this.userIsAdmin = true;
                this.userIsSuperAdmin = (user.email == 'test@example.com');
            }
        });

        // load users for masking function
        // TODO: this is broken - not all users listed under locations show up in this collection (??)
        // Workaround: add users as needed in Firestore backend, so we can mask as them to debug
        this.usersCollection = this.afs.collection<any>(`/users`);
        this.users = this.usersCollection.valueChanges();

        if (user) {
            this.loadUserLocations();
        }
    }

    // NB: we do not actually do Firebase authentication when masking as another user.
    // Also, this masking mechanism assumes that all user-specific data/state lives in their list of Locations
    // (thinning_datasets are loaded on demand based on location keys).
    maskAsUser(email: string) {
        console.log('masking as', email);
        this.maskingAdminEmail = this.userEmail;
        this.maskingAdminKey = this.userKey;
        this.userEmail = email;
        this.userKey = email;
        this.userIsAdmin = false;
        this.loadUserLocations();
    }

    unmask() {
        this.userEmail = this.maskingAdminEmail;
        this.userKey = this.maskingAdminKey;
        this.userIsAdmin = true;
        this.maskingAdminEmail = null;
        this.maskingAdminKey = null;
        this.loadUserLocations();
    }

    // add a newly registered user to the database
    createUser(email: string) {
        this.usersCollection.doc(email).set({email: email});
        this.userIsAdmin = false;
        this.userIsSuperAdmin = false;
    }

    loadUserLocations() {
        console.log('loadUserLocations', this.userKey);
        this.locationsCollection = this.afs.collection<Location>(`/locations/${this.userKey}/locations`);
        this.locations = this.locationsCollection.valueChanges();
    }

    getLocationDoc(location: Location) {
        return this.afs.doc(`/locations/${this.userKey}/locations/${location.id}`);
    }

    getLocationTitle(location: Location) {
        return `${location.farm} - ${location.block} - ${location.variety} (${location.year})`;
    }

    addLocation(location: Location) {
        const id = this.afs.createId();
        location.id = id;
        this.locationsCollection.doc(id).set(location);
    }

    updateLocation(location: Location) {
        this.locationsCollection.doc(location.id).set(location);
    }

    removeLocation(location: Location) {
        this.locationsCollection.doc(location.id).delete();
    }

    getThinningDatasetDoc(location: Location) {
        // thinning datasets use the same ID as the location they belong to
        return this.afs.doc(`/thinning_datasets/${this.userKey}/thinning_datasets/${location.id}`);
    }

    addThinningDataset(dataset: ThinningDataset) {
        // NB: dataset ID is set in location-detail.ts
        const doc = this.afs.doc(`/thinning_datasets/${this.userKey}/thinning_datasets/${dataset.id}`);
        doc.set(dataset);
    }

    updateThinningDataset(dataset: ThinningDataset) {
        const doc = this.afs.doc(`/thinning_datasets/${this.userKey}/thinning_datasets/${dataset.id}`);
        doc.set(dataset);
    }

    getThinningMeasurementsCollection(dataset: ThinningDataset) {
        return this.afs.collection<ThinningMeasurement>(
            `/thinning_datasets/${this.userKey}/thinning_datasets/${dataset.id}/measurements`,
            ref => ref.orderBy('created', 'asc')
        );
    }

    // creates a new measurement, saves it and returns it
    addNewThinningMeasurement(dataset: ThinningDataset) {
        let meas = new ThinningMeasurement({});

        meas.created = new Date().toISOString();
        meas.id = meas.created.substring(0, 10); // trim extra gunk to get YYYY-MM-DD

        // important: add placeholder to force creation of node in Firebase
        meas.data = {'1_1_1': 0};
        meas.summary = {predicted_pct_setting: 0};

        const measurementsCollection = this.afs.collection<ThinningMeasurement>(`/thinning_datasets/${this.userKey}/thinning_datasets/${dataset.id}/measurements`);
        measurementsCollection.doc(meas.id).set(Object.assign({}, meas));
        return meas;
    }

    // saves the given measurement for the first time
    addThinningMeasurement(dataset: ThinningDataset, meas: ThinningMeasurement) {
        const measurementsCollection = this.afs.collection<ThinningMeasurement>(`/thinning_datasets/${this.userKey}/thinning_datasets/${dataset.id}/measurements`);
        measurementsCollection.doc(meas.id).set(Object.assign({}, meas));
    }

    updateThinningMeasurement(dataset: ThinningDataset, meas: ThinningMeasurement) {
        const doc = this.afs.doc(`/thinning_datasets/${this.userKey}/thinning_datasets/${dataset.id}/measurements/${meas.id}`);
        meas.summary = Object.assign({}, meas.summary); // prevent Firestore error re: saving custom object
        doc.set(Object.assign({}, meas));
    }

    removeThinningMeasurement(dataset: ThinningDataset, meas: ThinningMeasurement) {
        const doc = this.afs.doc(`/thinning_datasets/${this.userKey}/thinning_datasets/${dataset.id}/measurements/${meas.id}`);
        doc.delete();
    }

    getSprayRecordsCollection(loc: Location) {
        return this.afs.collection<SprayRecord>(
            `/locations/${this.userKey}/locations/${loc.id}/spray_records`,
            ref => ref.orderBy('date', 'asc')
        );
    }

    addSprayRecord(loc: Location, record: SprayRecord) {
        const id = this.afs.createId();
        record.id = id;
        const sprayRecordsCollection = this.afs.collection<SprayRecord>(`/locations/${this.userKey}/locations/${loc.id}/spray_records`);
        sprayRecordsCollection.doc(record.id).set(record);
    }

    updateSprayRecord(loc: Location, record: SprayRecord) {
        const sprayRecordsCollection = this.afs.collection<SprayRecord>(`/locations/${this.userKey}/locations/${loc.id}/spray_records`);
        sprayRecordsCollection.doc(record.id).set(record);
    }

    removeSprayRecord(loc: Location, record: SprayRecord) {
        const sprayRecordsCollection = this.afs.collection<SprayRecord>(`/locations/${this.userKey}/locations/${loc.id}/spray_records`);
        sprayRecordsCollection.doc(record.id).delete();
    }

    getIrrigationRecordsCollection(loc: Location) {
        return this.afs.collection<IrrigationRecord>(
            `/irrigation_records/${this.userKey}/irrigation_records/${loc.id}/records`,
            ref => ref.orderBy('date', 'asc')
        );
    }

    addIrrigationRecord(loc: Location, record: IrrigationRecord) {
        // NB: record ID (YYYY-MM-DD) is set in IrrigationRecord constructor - see updateIrrigationRecords
        const irrigationRecordsCollection = this.afs.collection<IrrigationRecord>(`/irrigation_records/${this.userKey}/irrigation_records/${loc.id}/records`);
        irrigationRecordsCollection.doc(record.id).set(Object.assign({}, record));
    }

    updateIrrigationRecord(loc: Location, record: IrrigationRecord) {
        const irrigationRecordsCollection = this.afs.collection<IrrigationRecord>(`/irrigation_records/${this.userKey}/irrigation_records/${loc.id}/records`);
        irrigationRecordsCollection.doc(record.id).set(Object.assign({}, record));
    }

    hasInvalidWeatherStationId(loc: Location) {
        // NB: these are the unique old station IDs in use at the moment - there are no doubt other invalid IDs...
        // (station ID structure has changed to include network within the ID)
        const oldIds = [
            '289',
            '3wi',
            'app',
            'ash',
            'bra',
            'cah',
            'chz',
            'clc',
            'czy',
            'ew_bel',
            'ew_clr',
            'ew_elb',
            'ew_msu',
            'ew_ner',
            'ew_nwm',
            'ew_sbe',
            'ew_spo',
            'ew_ver',
            'gbe',
            'gen',
            'ith',
            'kalb',
            'kaoh',
            'kavl',
            'kbgm',
            'ken',
            'keri',
            'khef',
            'kiag',
            'kmsp',
            'kmss',
            'kno',
            'krme',
            'ma_nort',
            'mbe',
            'mil',
            'mlc',
            'mst',
            'nc_eaw',
            'nc_fla',
            'nc_gre',
            'nj_mil',
            'noa',
            'ny_genn',
            'ny_lans',
            'nysm_burt',
            'nysm_laur',
            'pa_big',
            'per',
            'pfo',
            'pno',
            'scl',
            'sdl',
            'um_cso',
            'va_win',
            'vco',
            'wal',
            'wi_tre',
            'wlk',
            'wtr',
        ]

        if (!loc.weather_station || oldIds.indexOf(loc.weather_station.id) != -1) {
            return true
        }
        return false
    }

    // --------------------- utils -----------------------

    // create an array for use with ngFor - e.g. range(1,5) => [1,2,3,4,5]
    range(m, n) {
        let r = [];
        for (let i = m; i <= n - m + 1; i++) {
            r.push(i);
        }
        return r;
    }

    // convert NaN to null (Firebase can't store NaN values)
    nullIfNaN(val) {
        return _.isNaN(val) ? null : val;
    }

    standardDeviation(values) {
        const mean = _.mean(values);
        // console.log(mean)
        const squareDiffs = _.map(values, (val) => {
            return Math.pow(val - mean, 2);
        })
        return Math.sqrt(
            _.mean(squareDiffs)
        );
    }

}



// WEBPACK FOOTER //
// ./src/providers/data.ts